import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

import RequirementChip from "@/domains/certifications/components/RequirementChip";
import { buildRequirementChips } from "@/domains/certifications/chips";
import {
  getWeekStatusLine,
  toWeekPill,
} from "@/features/certifications/participant-detail/utils";
import { WeekProgressProps } from "@/features/certifications/participant-detail/types";
import { WEEK_PILLS } from "@/features/certifications/participant-detail/constants";

import "../styles.css";

const CHECK_ICON_SIZE = 12;

const WeekProgress = ({
  weeks,
  progress,
  weeksCompleted,
}: WeekProgressProps) => {
  const { t } = useTranslation();
  const isComplete = weeks.length > 0 && weeksCompleted >= weeks.length;

  return (
    <section className="certification-card participant-detail-card flex flex-col gap-300">
      <header className="flex flex-wrap items-baseline justify-between gap-200">
        <h2 className="certification-heading body-m-medium">
          {t("certification.admin.participant.progressTitle")}
        </h2>

        <span
          className={`body-s-medium ${
            isComplete ? "participant-detail-done" : "participant-detail-open"
          }`}
        >
          {t("certification.admin.participant.weeksComplete", {
            count: weeksCompleted,
            total: weeks.length,
          })}
        </span>
      </header>

      <div className="flex gap-100" aria-hidden>
        {weeks.map((week) => (
          <span
            key={week.module.id}
            className={`participant-detail-pill participant-detail-pill-${toWeekPill(week)} flex-1`}
          />
        ))}
      </div>

      <ol className="participant-detail-weeks flex flex-col">
        {weeks.map((week) => {
          const pill = toWeekPill(week);
          const moduleProgress = progress.modules.find(
            ({ id }) => id === week.module.id,
          );

          return (
            <li
              key={week.module.id}
              className="participant-detail-week flex flex-wrap items-center gap-300"
            >
              <span
                className={`participant-detail-disc participant-detail-disc-${pill} flex flex-none items-center justify-center`}
                aria-hidden
              >
                {pill === WEEK_PILLS.COMPLETE && (
                  <Check size={CHECK_ICON_SIZE} />
                )}
              </span>

              <div className="flex flex-col gap-100 flex-1">
                <span
                  className={`body-s-medium ${
                    pill === WEEK_PILLS.FUTURE ? "certification-muted" : ""
                  }`}
                >
                  {t("certification.admin.participant.weekTitle", {
                    week: week.position,
                    title: week.module.title,
                  })}
                </span>

                <div className="flex flex-wrap gap-100">
                  {buildRequirementChips(t, week).map((chip) => (
                    <RequirementChip key={chip.label} {...chip} />
                  ))}
                </div>
              </div>

              <span className="certification-muted body-xs-regular">
                {getWeekStatusLine(t, week, moduleProgress)}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default WeekProgress;
